import { useEffect, useMemo, useState, useRef } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  createColumnHelper,
  SortingState,
  ColumnFiltersState,
  flexRender,
} from "@tanstack/react-table";
import PageLayout from "../components/PageLayout";
import TableInput from "../components/TableInput";
import Dropdown from "../components/Dropdown";
import Badge from "../components/Badge";
import Tooltip from "../components/Tooltip";
import {
  tanstackTableStyle,
  getTanstackThStyle,
  getTanstackTdStyle,
  sortHeaderStyle,
  sortArrowStyle,
  filterContainerStyle,
  tanstackTrStyle,
  emptyRowStyle,
} from "../components/Style";
import { apiClient } from "../services/api";

const MSG_MAX_LEN = 30; // 이 글자 수 초과 시 말줄임 + 팝오버

interface MessageHistoryRow {
  id: number;
  teacherName: string | null;
  className: string | null;
  studentName: string | null;
  recipientLabel: string | null;
  receiver: string;
  msg: string;
  testmode: boolean;
  isBulk: boolean;
  success: boolean;
  performedBy: string | null;
  createdAt: string;
}

const columnHelper = createColumnHelper<MessageHistoryRow>();

const MULTILINE_TOOLTIP_STYLE: React.CSSProperties = {
  whiteSpace: "pre-line",
  textAlign: "left",
  fontSize: 12,
  lineHeight: 1.7,
};

function formatDate(iso: string) {
  if (!iso) return "-";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/* ───── 메시지 팝오버 컴포넌트 ───── */
function MessagePopover({
  msg,
  anchorRef,
  onClose,
}: {
  msg: string;
  anchorRef: React.RefObject<HTMLElement>;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        popRef.current &&
        !popRef.current.contains(e.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose, anchorRef]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(msg);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      ref={popRef}
      style={{
        position: "fixed",
        zIndex: 9999,
        background: "#fff",
        border: "1px solid #d0d7de",
        borderRadius: 8,
        boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
        padding: "14px 16px 12px",
        maxWidth: 360,
        minWidth: 220,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        style={{
          fontSize: 13,
          color: "#222",
          whiteSpace: "pre-wrap",
          lineHeight: 1.65,
          marginBottom: 10,
          maxHeight: 260,
          overflowY: "auto",
        }}
      >
        {msg}
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          onClick={handleCopy}
          style={{
            fontSize: 12,
            padding: "4px 12px",
            borderRadius: 6,
            border: "1px solid #d0d7de",
            background: copied ? "#e6ffed" : "#f6f8fa",
            color: copied ? "#1a7f37" : "#24292f",
            cursor: "pointer",
            fontWeight: 600,
            transition: "background 0.2s",
          }}
        >
          {copied ? "✓ 복사됨" : "클립보드로 복사"}
        </button>
      </div>
    </div>
  );
}

/* ───── 메시지 셀 컴포넌트 ───── */
function MessageCell({ msg }: { msg: string }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const cellRef = useRef<HTMLSpanElement>(null);
  const flat = msg.replace(/[\n\r]+/g, ' ');
  const isTruncated = flat.length > MSG_MAX_LEN;
  const display = isTruncated ? flat.slice(0, MSG_MAX_LEN) + "…" : flat;

  const handleClick = (e: React.MouseEvent) => {
    if (!isTruncated) return;
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setPos({
      top: rect.bottom + 6,
      left: Math.min(rect.left, window.innerWidth - 380),
    });
    setOpen(true);
  };

  return (
    <>
      <span
        ref={cellRef}
        onClick={handleClick}
        title={isTruncated ? "클릭하여 전체 메시지 보기" : undefined}
        style={{
          fontSize: 13,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block",
          cursor: isTruncated ? "pointer" : "default",
          color: isTruncated ? "#0969da" : undefined,
          textDecoration: isTruncated ? "underline dotted" : undefined,
        }}
      >
        {display}
      </span>
      {open && (
        <div style={{ position: "fixed", top: pos.top, left: pos.left }}>
          <MessagePopover
            msg={msg}
            anchorRef={cellRef as React.RefObject<HTMLElement>}
            onClose={() => setOpen(false)}
          />
        </div>
      )}
    </>
  );
}

/* ───── 페이지 ───── */
export default function MessageHistoryPage() {
  const [rows, setRows] = useState<MessageHistoryRow[]>([]);
  const [teacherFilter, setTeacherFilter] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [sorting, setSorting] = useState<SortingState>([{ id: "createdAt", desc: true }]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  useEffect(() => {
    apiClient.getMessageHistory().then(setRows).catch(console.error);
  }, []);

  useEffect(() => {
    setClassFilter("");
  }, [teacherFilter]);

  const teacherOptions = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => { if (r.teacherName) set.add(r.teacherName); });
    return Array.from(set).sort();
  }, [rows]);

  const classOptions = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => {
      if (teacherFilter && r.teacherName !== teacherFilter) return;
      if (r.className) set.add(r.className);
    });
    return Array.from(set).sort();
  }, [rows, teacherFilter]);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (teacherFilter && r.teacherName !== teacherFilter) return false;
      if (classFilter && r.className !== classFilter) return false;
      return true;
    });
  }, [rows, teacherFilter, classFilter]);

  const columns = useMemo(() => [
    columnHelper.accessor("teacherName", {
      header: "선생님",
      size: 80,
      cell: (info) => <span style={{ fontSize: 13 }}>{info.getValue() || "-"}</span>,
      sortingFn: "alphanumeric",
    }),
    columnHelper.accessor("className", {
      header: "반",
      size: 120,
      cell: (info) => <span style={{ fontSize: 13 }}>{info.getValue() || "-"}</span>,
      sortingFn: "alphanumeric",
    }),
    columnHelper.accessor("studentName", {
      header: "이름",
      size: 80,
      cell: (info) => <span style={{ fontSize: 13 }}>{info.getValue() || "-"}</span>,
      sortingFn: "alphanumeric",
      filterFn: "includesString",
    }),
    columnHelper.accessor("recipientLabel", {
      header: "메시지 수신자",
      size: 80,
      cell: (info) => <span style={{ fontSize: 13 }}>{info.getValue() || "-"}</span>,
    }),
    columnHelper.accessor("receiver", {
      header: "메시지 수신번호",
      size: 120,
      cell: (info) => <span style={{ fontSize: 13 }}>{info.getValue()}</span>,
    }),
    columnHelper.accessor("msg", {
      header: "메시지 내용",
      size: 280,
      cell: (info) => <MessageCell msg={info.getValue()} />,
      enableSorting: false,
    }),
    columnHelper.accessor("isBulk", {
      header: () => (
        <Tooltip
          content={"단수: 1명에게 전송\n복수: 2명 이상에게 전송"}
          contentStyle={MULTILINE_TOOLTIP_STYLE}
        >
          <span style={{ cursor: "help", borderBottom: "1px dotted #9ca3af" }}>단수</span>
        </Tooltip>
      ),
      size: 60,
      cell: (info) => info.getValue()
        ? <Badge variant="primary">복수</Badge>
        : <Badge variant="secondary">단수</Badge>,
      enableSorting: false,
    }),
    columnHelper.accessor("testmode", {
      header: () => (
        <Tooltip
          content={"실제: 실제로 전송함\n테스트: 실제로 보내기 직전까지를 테스트함"}
          contentStyle={MULTILINE_TOOLTIP_STYLE}
        >
          <span style={{ cursor: "help", borderBottom: "1px dotted #9ca3af" }}>실제</span>
        </Tooltip>
      ),
      size: 65,
      cell: (info) => info.getValue()
        ? <Badge variant="warning">테스트</Badge>
        : <Badge variant="success">실제</Badge>,
      enableSorting: false,
    }),
    columnHelper.accessor("success", {
      header: "성공",
      size: 55,
      cell: (info) => info.getValue()
        ? <Badge variant="success">성공</Badge>
        : <Badge variant="danger">실패</Badge>,
      enableSorting: false,
    }),
    columnHelper.accessor("createdAt", {
      header: "발송 일시",
      size: 140,
      cell: (info) => <span style={{ fontSize: 13 }}>{formatDate(info.getValue())}</span>,
      sortingFn: "alphanumeric",
    }),
  ], []);

  const table = useReactTable({
    data: filteredRows,
    columns,
    state: { sorting, columnFilters },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <PageLayout title="메시지 이력">
      <div style={{ overflow: "auto", maxHeight: "calc(100vh - 180px)" }}>
        <table style={tanstackTableStyle}>
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => (
                  <th key={header.id} style={getTanstackThStyle(false, header.getSize())}>
                    {header.isPlaceholder ? null : (
                      <div>
                        {header.column.getCanSort() ? (
                          <div onClick={header.column.getToggleSortingHandler()} style={sortHeaderStyle}>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {{ asc: <span style={sortArrowStyle}>▲</span>, desc: <span style={sortArrowStyle}>▼</span> }[header.column.getIsSorted() as string] ?? null}
                          </div>
                        ) : (
                          flexRender(header.column.columnDef.header, header.getContext())
                        )}
                        <div style={filterContainerStyle}>
                          {header.column.id === "teacherName" && (
                            <Dropdown
                              value={teacherFilter}
                              onChange={setTeacherFilter}
                              options={[
                                { value: "", label: "전체" },
                                ...teacherOptions.map((t) => ({ value: t, label: t })),
                              ]}
                            />
                          )}
                          {header.column.id === "className" && (
                            <Dropdown
                              value={classFilter}
                              onChange={setClassFilter}
                              options={[
                                { value: "", label: "전체" },
                                ...classOptions.map((c) => ({ value: c, label: c })),
                              ]}
                            />
                          )}
                          {header.column.id === "studentName" && (
                            <TableInput
                              value={(header.column.getFilterValue() as string) ?? ""}
                              onChange={(e) => header.column.setFilterValue(e.target.value)}
                              placeholder="검색"
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} style={emptyRowStyle}>메시지 이력이 없습니다.</td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} style={tanstackTrStyle}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} style={{ ...getTanstackTdStyle(false, cell.column.getSize()), textAlign: "center" }}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </PageLayout>
  );
}

